import * as icons from '../../components/assets/images/Icons';
import { routeBy } from '../../components/flows/FlowNavigation';
import { CoverageCheckLayoutConfig } from '../../components/flows/InsuranceProviderScreen';
import { CoverageResultsLayoutConfig } from '../../components/flows/layouts/CoverageResultsLayout';
import { PartnerInsurerAccessLayoutConfig } from '../../components/flows/layouts/PartnerInsurerAccessLayout';
import { getCoverageAddressRoute } from '../../util/CoverageSelectors';
import {
  CoverageScreenName,
  MedicareAccessScreenName,
  ScreenConfig,
  ScreenType,
} from '../FlowTypes';
import {
  COVERAGE_BENEFIT_NONE,
  COVERAGE_BENEFIT_NOT_SURE,
  COVERAGE_PLAN_TYPE_EMPLOYER,
  COVERAGE_PLAN_TYPE_MEDICAID,
  COVERAGE_PLAN_TYPE_NOT_SURE,
  COVERAGE_PLAN_TYPE_SELF_PURCHASED,
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  HEALTH_COVER_MEDICARE_ORIGINAL,
} from '../InsuranceProviders';
import { MEDICARE_ACCESS_STATE_LAYOUT } from './MedicareAccessConfig';
import { NEXT_BUTTON_TITLE } from './NervaControlScreenNames';

/**
 * US under-65 (MIS-1) and AU (MIS-177) insurance coverage screens, shared by the Control and
 * HCP-referred Personalise configs. Entered from the insurance-provider dropdown via
 * `getCoverageEntryRoute`; the US address screen and the AU membership-number screen exit to the
 * consents (US Original Medicare without an address answers the state question first), a timed
 * loader, then `CoverageResults`, which each config declares itself because the onward route differs
 * (Control → Auth, Personalise → referral/payment map). Screen names double as `user_data` keys
 * (except the consents and loader screens'), so they are stable; stored plan-type values come from
 * `InsuranceProviders.ts`.
 */
export const COVERAGE_SCREENS: Array<
  ScreenConfig<CoverageScreenName | MedicareAccessScreenName.MedicareAccessStateNoAddress>
> = [
  {
    screenName: CoverageScreenName.CoveragePlanType,
    type: ScreenType.SingleSelect,
    layout: {
      body: ['How do you get your health insurance?'],
      choices: [
        {
          value: COVERAGE_PLAN_TYPE_EMPLOYER,
          label: 'Through my employer (or a family member’s employer)',
        },
        { value: COVERAGE_PLAN_TYPE_SELF_PURCHASED, label: 'I bought it myself' },
        HEALTH_COVER_MEDICARE_ORIGINAL,
        HEALTH_COVER_MEDICARE_ADVANTAGE,
        COVERAGE_PLAN_TYPE_MEDICAID,
        { value: COVERAGE_PLAN_TYPE_NOT_SURE, label: 'I’m not sure' },
      ],
    },
    // Medicare / Medicaid users skip the employer and benefits questions and go straight to the
    // address; the results screen (and isMedicareOriginal) pick the ACCESS pathway up from there.
    nextScreen: {
      [COVERAGE_PLAN_TYPE_EMPLOYER]: CoverageScreenName.CoverageUsEmployer,
      [COVERAGE_PLAN_TYPE_SELF_PURCHASED]: CoverageScreenName.CoverageBenefits,
      [COVERAGE_PLAN_TYPE_NOT_SURE]: CoverageScreenName.CoverageBenefits,
      [HEALTH_COVER_MEDICARE_ORIGINAL]: CoverageScreenName.CoverageAddress,
      [HEALTH_COVER_MEDICARE_ADVANTAGE]: CoverageScreenName.CoverageAddress,
      [COVERAGE_PLAN_TYPE_MEDICAID]: CoverageScreenName.CoverageAddress,
      fallback: CoverageScreenName.CoverageAddress,
    },
  },
  {
    screenName: CoverageScreenName.CoverageUsEmployer,
    type: ScreenType.CoverageEmployer,
    layout: {
      title: 'Who is your employer?',
      body: [
        'Your employer usually decides the benefits list, so this is the most useful thing you can tell us.',
      ],
      inputLabel: 'Company name',
      inputPlaceholder: 'Company name',
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: CoverageScreenName.CoverageBenefits,
  },
  {
    screenName: CoverageScreenName.CoverageBenefits,
    type: ScreenType.MultiSelect,
    layout: {
      body: ['Do you have any of these benefits?'],
      choices: [
        'HSA (Health Savings Account)',
        'FSA (Flexible Spending Account)',
        'Wellness stipend from my employer',
        COVERAGE_BENEFIT_NONE,
        COVERAGE_BENEFIT_NOT_SURE,
      ],
      exclusiveChoices: [COVERAGE_BENEFIT_NONE, COVERAGE_BENEFIT_NOT_SURE],
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    nextScreen: CoverageScreenName.CoverageAddress,
  },
  {
    screenName: CoverageScreenName.CoverageAddress,
    type: ScreenType.CoverageAddress,
    layout: {
      title: 'What’s your address?',
      addressLabel: 'Street address',
      addressPlaceholder: 'Start typing your address',
      notFoundButtonTitle: 'I can’t find my address',
      infoBox: {
        titleText: 'Why do we ask this?',
        bodyText:
          'Your health plan needs your address to confirm your coverage and process any claims. Your information is private and secure.',
      },
      primaryButtonTitle: NEXT_BUTTON_TITLE,
    },
    ...routeBy(getCoverageAddressRoute, {
      askState: MedicareAccessScreenName.MedicareAccessStateNoAddress,
      fallback: CoverageScreenName.CoverageConsents,
    }),
  },
  {
    // The address state decides ACCESS, so Original Medicare users who couldn't find their address
    // are asked for it here; the results screen then makes the ACCESS decision.
    screenName: MedicareAccessScreenName.MedicareAccessStateNoAddress,
    type: ScreenType.DropdownSelect,
    layout: MEDICARE_ACCESS_STATE_LAYOUT,
    nextScreen: CoverageScreenName.CoverageConsents,
  },
  {
    // AU users who named their insurer. Optional: the coverage check is filed without it.
    screenName: CoverageScreenName.CoverageMemberId,
    type: ScreenType.SingleInput,
    layout: {
      type: 'string',
      collectsPIIData: true,
      content: [
        'What’s your membership number?',
        'It’s on your private health insurance membership card. It helps your insurer find your policy.',
      ],
      inputLabel: 'Membership number',
      inputPlaceholder: 'As shown on your card',
      primaryButtonTitle: NEXT_BUTTON_TITLE,
      skipButtonTitle: 'Skip',
    },
    nextScreen: CoverageScreenName.CoverageConsents,
  },
  {
    // Every consent, asked once the user has given their details. All optional: the check is only
    // filed if they tick the health plan or employer consent.
    screenName: CoverageScreenName.CoverageConsents,
    type: ScreenType.CoverageConsents,
    layout: {
      title: 'Can we help get Nerva covered?',
      body: 'These are optional. You can start your trial either way.',
      showFullTitle: 'Show full details',
      hideFullTitle: 'Hide full details',
      primaryButtonTitle: 'Continue',
      us: [
        {
          consent: 'healthPlan',
          summary: 'Help me get Nerva covered by contacting my health plan.',
          fullText: [
            'I authorize Mindset Health Inc. ("Nerva") to use and share my name, date of birth, address, insurance details, the fact that I use Nerva (a gut-brain program for IBS and related gut conditions), coverage information my plan returns, and clinical documentation from my referring clinician, including a letter of medical necessity. Psychotherapy notes are excluded.',
            'With my health plan and its administrators, to: verify eligibility through Nerva’s clearinghouse partner; request coverage, predeterminations, authorizations and exceptions; submit claims and appeals and act as my representative where the plan permits; and ask my plan to cover Nerva, including letting it evaluate Nerva for its members.',
            'Term and revocation: valid while I have a Nerva account. I can revoke it any time by emailing privacy@mindsethealth.com. Revocation doesn’t affect actions already taken in reliance on it.',
            'My rights: optional, and doesn’t affect my access to Nerva or my price. Nerva won’t sell this information or use it for advertising. My plan remains bound by federal health privacy law; information may be redisclosed by recipients as their rules allow.',
            'Ticking the box is my electronic signature.',
          ],
        },
        {
          consent: 'assignmentOfBenefits',
          summary:
            'If my plan ever pays for Nerva, pay Nerva directly. If it pays for time I’ve already paid for, Nerva refunds me.',
          fullText: [
            'If my plan ever pays for Nerva, pay Nerva directly. I assign any benefits payable for Nerva to Mindset Health Inc. and authorize it to receive payment from my plan and to submit claims and predeterminations with my signature on file. I pay Nerva’s listed price; if my plan pays Nerva for a period I’ve already paid for, Nerva refunds me.',
            'Ticking the box is my electronic signature.',
          ],
        },
        {
          consent: 'employer',
          summary:
            'Also ask my employer’s benefits team and broker to cover Nerva. They’ll see my name and my request.',
          fullText: [
            'I authorize Mindset Health Inc. ("Nerva") to share my name, my employer, and the fact that I asked for Nerva (a gut-brain program for IBS and related gut conditions) to be covered.',
            'With my employer’s health benefits and HR teams, and the benefits brokers, consultants and plan administrators who help my employer choose or run its benefits, to: tell them I asked for Nerva; confirm my request is genuine if they ask; answer their questions about it; and ask them to add or purchase Nerva.',
            'Term and revocation: valid for 12 months from today. I can revoke it any time by emailing privacy@mindsethealth.com. Revocation doesn’t affect messages already sent.',
            'My rights: optional, and doesn’t affect my access to Nerva or my price. Nerva won’t sell this information. My employer and its advisers aren’t bound by federal health privacy law once they receive it, and may share it further.',
            'Ticking the box is my electronic signature. I can ask for a copy of this authorization at privacy@mindsethealth.com.',
          ],
        },
      ],
      au: [
        {
          consent: 'healthPlan',
          summary: 'Help me get Nerva covered by contacting my private health insurer.',
          fullText: [
            'I consent to Nerva using the information I have provided to contact my health plan, insurer, or employer’s benefits team, as applicable, to seek coverage or reimbursement for my use of Nerva. This may include sharing my name, employer (if applicable), health-plan or insurance information, and that I use or intend to use Nerva. See our Privacy Policy for how we handle my information.',
          ],
          privacyPolicyTitle: 'Privacy Policy',
        },
      ],
    },
    nextScreen: CoverageScreenName.CoverageLoading,
  },
  {
    screenName: CoverageScreenName.CoverageLoading,
    type: ScreenType.AiLoading,
    // The check is filed on the consents screen; answers changed after it would never reach it.
    resetHistoryOnEntry: true,
    layout: {
      title: 'Checking your coverage',
      subtitle: 'This will only take a moment.',
      showAiBadge: false,
      loadingBars: [
        {
          title: 'Saving your details',
          icon: icons.document,
          startNumber: 0,
          endNumber: 100,
          durationMs: 1500,
        },
        {
          title: 'Matching your plan to covered plans',
          icon: icons.filters,
          startNumber: 0,
          endNumber: 100,
          durationMs: 2500,
        },
        {
          title: 'Preparing your results',
          icon: icons.sparkles,
          startNumber: 0,
          endNumber: 100,
          durationMs: 1500,
        },
      ],
    },
    nextScreen: CoverageScreenName.CoverageResults,
  },
];

/**
 * "Check my coverage" variant of the insurance-provider dropdown, applied on the Control and
 * Personalise entry screens for US non-65+ users. The button enables once a provider is picked (the
 * consents come later, on the consents screen); the self-pay link skips the coverage flow (see
 * getCoverageEntryRoute / hasOptedForSelfPay).
 */
export const COVERAGE_CHECK_LAYOUT: CoverageCheckLayoutConfig = {
  title: 'Now let’s check if you are covered by your insurance.',
  question: 'Who’s your health insurance provider?',
  primaryButtonTitle: 'Check my coverage',
  selfPayTitle: 'I don’t want to use my insurance',
};

/**
 * AU variant of {@link COVERAGE_CHECK_LAYOUT} for the same entry screens, with a second exit for
 * users without private health insurance (Medicare isn't in the insurer list).
 */
export const AU_COVERAGE_CHECK_LAYOUT: CoverageCheckLayoutConfig = {
  title: 'Now let’s check if you’re covered by your private health insurance.',
  question: 'Who’s your private health insurer?',
  primaryButtonTitle: 'Check my coverage',
  noPrivateInsuranceTitle: 'I don’t have private health insurance',
  selfPayTitle: 'I don’t want to use my private health insurance',
};

/**
 * No-cost access offer shown instead of the coverage check when the user picks a partner insurer
 * (`NERVA_PARTNER_INSURERS`). Each config declares the screen itself because the onward route
 * differs (Control → Payment or the money-back guarantee, Personalise → Payment or the referral route).
 */
export const PARTNER_INSURER_ACCESS_LAYOUT: PartnerInsurerAccessLayoutConfig = {
  title: 'Get Nerva at no cost with {{insurer}}',
  body: [
    '{{insurer}} members can use Nerva at no cost. Enter your member ID and we’ll confirm your eligibility with {{insurer}}.',
  ],
  inputLabel: '{{insurer}} member ID',
  inputPlaceholder: 'As shown on your card',
  consentLabel:
    'I consent to Nerva sharing my {{insurer}} member ID with {{insurer}} to confirm my eligibility for no-cost access.',
  primaryButtonTitle: 'Use my {{insurer}} access',
  secondaryButtonTitle: 'Continue without {{insurer}} access',
};

/**
 * Coverage results shown after the US address step or the AU membership number. Shared so both
 * configs (and both countries) show the same copy while each keeps its own onward route. Only
 * Control shows the assistance card (via `assistanceScreen`); HCP-referred users don't see PAP here.
 */
export const COVERAGE_RESULTS_LAYOUT: CoverageResultsLayoutConfig = {
  title: 'You may not be covered yet.',
  subtitle: 'Plans decide what they cover, and lists change when members ask.',
  nextSteps: {
    heading: 'What happens next',
    body: [
      'We’ll email you a ready-to-send request for your insurer, so you can forward it whenever works for you.',
      'There’s nothing you need to do right now.',
    ],
    emailLead: 'It’ll go to',
  },
  assistance: {
    heading: 'Other financial assistance',
    title: 'Patient Assistance Program',
    body: 'Means-tested support that can reduce the cost, in some cases to nothing.',
    linkTitle: 'See how',
  },
  primaryButtonTitle: 'Start free trial',
};
